/**
 * Versa customer questionnaire payloads.
 * Used by `components/questionnaire/products/VersaQuestionnaire.tsx`.
 */

/** Stay defaults — general timing & timezone */
export interface StayDefaultsGeneralPayload {
  checkInTime: string | null;
  checkOutTime: string | null;
  timezone: string | null;
}

/** Stay defaults — booking limits */
export interface StayDefaultsBookingConstraintsPayload {
  minStay: number | null;
  maxStay: number | null;
  minLeadTime: number | null;
}

/** Stay defaults — cancellation */
export interface StayDefaultsCancellationPayload {
  policyType: string | null;
  refundPercent: number | null;
  gracePeriodHours: number | null;
}

/** Stay defaults — guest rules */
export interface StayDefaultsGuestRequirementsPayload {
  minAge: number | null;
  idRequired: boolean;
  photoRequired: boolean;
}

export interface StayDefaultsPayload {
  general: StayDefaultsGeneralPayload;
  bookingConstraints: StayDefaultsBookingConstraintsPayload;
  cancellation: StayDefaultsCancellationPayload;
  guestRequirements: StayDefaultsGuestRequirementsPayload;
}

export const CANCELLATION_POLICY_OPTIONS = [
  "Flexible",
  "Moderate",
  "Strict",
] as const;

export type CancellationPolicyType =
  (typeof CANCELLATION_POLICY_OPTIONS)[number];

export const PROPERTY_TIMEZONE_OPTIONS: string[] = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

export interface AgeGroupBand {
  code: string;
  label: string;
  minAgeInclusive: number | null;
  maxAgeInclusive: number | null;
}

export interface AgeGroupsPayload {
  bands: AgeGroupBand[];
}

/** Required fields — which guest / reservation fields are mandatory */
export interface RequiredFieldsPayload {
  emailRequired: boolean;
  phoneRequired: boolean;
  mailingAddressRequired: boolean;
  vehicleRegistrationRequired: boolean;
  emergencyContactRequired: boolean;
  notes: string | null;
}

/** Default codes — system default code values */
export interface DefaultCodesPayload {
  defaultMarketCode: string | null;
  defaultSourceCode: string | null;
  defaultOriginCode: string | null;
  defaultPurposeOfStayCode: string | null;
  defaultGuaranteeCode: string | null;
  notes: string | null;
}

/** System defaults — property-wide system behavior */
export interface SystemDefaultsPayload {
  businessDayStartTime: string | null;
  currencyCode: string | null;
  dateFormatCode: string | null;
  fiscalYearStartMonth: number | null;
  localeCode: string | null;
  notes: string | null;
}

/** Housekeeping defaults */
export interface HousekeepingDefaultsPayload {
  defaultServiceFrequencyCode: string | null;
  fullCleanIntervalNights: number | null;
  turndownServiceDefault: boolean;
  respectDoNotDisturbForService: boolean;
  notes: string | null;
}

/** Configuring group defaults */
export interface ConfiguringGroupDefaultsPayload {
  defaultConfigurationGroupCode: string | null;
  defaultInventoryGroupCode: string | null;
  defaultPricingGroupCode: string | null;
  allowSplitInventoryAcrossGroups: boolean;
  notes: string | null;
}

const emptyGeneral = (): StayDefaultsGeneralPayload => ({
  checkInTime: null,
  checkOutTime: null,
  timezone: null,
});

const emptyBookingConstraints = (): StayDefaultsBookingConstraintsPayload => ({
  minStay: null,
  maxStay: null,
  minLeadTime: null,
});

const emptyCancellation = (): StayDefaultsCancellationPayload => ({
  policyType: null,
  refundPercent: null,
  gracePeriodHours: null,
});

const emptyGuestRequirements = (): StayDefaultsGuestRequirementsPayload => ({
  minAge: null,
  idRequired: false,
  photoRequired: false,
});

export const initialStayDefaultsPayload = (): StayDefaultsPayload => ({
  general: emptyGeneral(),
  bookingConstraints: emptyBookingConstraints(),
  cancellation: emptyCancellation(),
  guestRequirements: emptyGuestRequirements(),
});

export const initialAgeGroupsPayload = (): AgeGroupsPayload => ({
  bands: [
    {
      code: "INFANT",
      label: "Infant",
      minAgeInclusive: 0,
      maxAgeInclusive: 2,
    },
    {
      code: "CHILD",
      label: "Child",
      minAgeInclusive: 3,
      maxAgeInclusive: 12,
    },
    {
      code: "ADULT",
      label: "Adult",
      minAgeInclusive: 13,
      maxAgeInclusive: null,
    },
  ],
});

export const initialRequiredFieldsPayload = (): RequiredFieldsPayload => ({
  emailRequired: false,
  phoneRequired: false,
  mailingAddressRequired: false,
  vehicleRegistrationRequired: false,
  emergencyContactRequired: false,
  notes: null,
});

export const initialDefaultCodesPayload = (): DefaultCodesPayload => ({
  defaultMarketCode: null,
  defaultSourceCode: null,
  defaultOriginCode: null,
  defaultPurposeOfStayCode: null,
  defaultGuaranteeCode: null,
  notes: null,
});

export const initialSystemDefaultsPayload = (): SystemDefaultsPayload => ({
  businessDayStartTime: null,
  currencyCode: null,
  dateFormatCode: null,
  fiscalYearStartMonth: null,
  localeCode: null,
  notes: null,
});

export const initialHousekeepingDefaultsPayload =
  (): HousekeepingDefaultsPayload => ({
    defaultServiceFrequencyCode: null,
    fullCleanIntervalNights: null,
    turndownServiceDefault: false,
    respectDoNotDisturbForService: true,
    notes: null,
  });

export const initialConfiguringGroupDefaultsPayload =
  (): ConfiguringGroupDefaultsPayload => ({
    defaultConfigurationGroupCode: null,
    defaultInventoryGroupCode: null,
    defaultPricingGroupCode: null,
    allowSplitInventoryAcrossGroups: false,
    notes: null,
  });

export interface QuestionnairePayload {
  stayDefaults: StayDefaultsPayload;
  ageGroups: AgeGroupsPayload;
  requiredFields: RequiredFieldsPayload;
  defaultCodes: DefaultCodesPayload;
  systemDefaults: SystemDefaultsPayload;
  housekeepingDefaults: HousekeepingDefaultsPayload;
  configuringGroupDefaults: ConfiguringGroupDefaultsPayload;
}

export const mergeQuestionnairePayload = (
  stayDefaults: StayDefaultsPayload,
  ageGroups: AgeGroupsPayload,
  requiredFields: RequiredFieldsPayload,
  defaultCodes: DefaultCodesPayload,
  systemDefaults: SystemDefaultsPayload,
  housekeepingDefaults: HousekeepingDefaultsPayload,
  configuringGroupDefaults: ConfiguringGroupDefaultsPayload,
): QuestionnairePayload => ({
  stayDefaults,
  ageGroups,
  requiredFields,
  defaultCodes,
  systemDefaults,
  housekeepingDefaults,
  configuringGroupDefaults,
});

export const mergePartialStayDefaults = (
  partial?: Partial<StayDefaultsPayload> | null,
): StayDefaultsPayload => {
  const base = initialStayDefaultsPayload();
  if (!partial) return base;
  return {
    general: { ...base.general, ...partial.general },
    bookingConstraints: {
      ...base.bookingConstraints,
      ...partial.bookingConstraints,
    },
    cancellation: { ...base.cancellation, ...partial.cancellation },
    guestRequirements: {
      ...base.guestRequirements,
      ...partial.guestRequirements,
    },
  };
};

export const mergePartialRequiredFields = (
  partial?: Partial<RequiredFieldsPayload> | null,
): RequiredFieldsPayload => ({
  ...initialRequiredFieldsPayload(),
  ...partial,
});

export const mergePartialDefaultCodes = (
  partial?: Partial<DefaultCodesPayload> | null,
): DefaultCodesPayload => ({
  ...initialDefaultCodesPayload(),
  ...partial,
});

export const mergePartialSystemDefaults = (
  partial?: Partial<SystemDefaultsPayload> | null,
): SystemDefaultsPayload => ({
  ...initialSystemDefaultsPayload(),
  ...partial,
});

export const mergePartialHousekeepingDefaults = (
  partial?: Partial<HousekeepingDefaultsPayload> | null,
): HousekeepingDefaultsPayload => ({
  ...initialHousekeepingDefaultsPayload(),
  ...partial,
});

export const mergePartialConfiguringGroupDefaults = (
  partial?: Partial<ConfiguringGroupDefaultsPayload> | null,
): ConfiguringGroupDefaultsPayload => ({
  ...initialConfiguringGroupDefaultsPayload(),
  ...partial,
});
